import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DOCTOR_HOW_IT_WORKS_PAGES } from '@medicusunion/front-utils';
import {
  DeleteOutlined,
  EyeOutlined,
  ShareAltOutlined
} from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { App, Button, Flex, Image, Modal, Table, Typography } from 'antd';
import {
  GetDicomDoctor,
  GetDicomDoctor_getDicomDoctor_data_results as DicomDoctorType
} from 'gql/sessions/__generated__/GetDicomDoctor';
import { DELETE_DICOM_LINK } from 'gql/sessions/mutations';
import { GET_DICOM } from 'gql/sessions/queries';
import { useAppDispatch } from 'store/hooks';
import { setTour } from 'store/tour/slice';
import { ColumnProps } from 'antd/lib/table';
import DeleteIcon from 'assets/icons/delete.svg';

import {
  ConfirmModalContent,
  HowItWorksLink
  // Password
} from 'components/shared';
import { CreateDicom } from './components';
import { PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';
import useCopyText from 'hooks/useCopyText';

const PAGE_LIMIT = 10;

const { Title } = Typography;

const Dicom: FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const { copyText } = useCopyText();

  const { message } = App.useApp();

  // const [isOpenModal, setIsOpenModal] = useState(false);
  const dispatch = useAppDispatch();
  const { data, loading, refetch } = useQuery<GetDicomDoctor>(GET_DICOM, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      query: {
        pagination: {
          page,
          limit: PAGE_LIMIT
        }
      }
    },
    onCompleted() {
      dispatch(setTour({ key: 'dicom', value: true }));
    }
  });

  const [deleteDicomLink, { loading: isDeleting }] = useMutation(
    DELETE_DICOM_LINK,
    {
      onCompleted() {
        refetch();
        Modal.destroyAll();
      },
      onError: err => showErrorMessage(err)
    }
  );

  const onClickDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        closable: !isDeleting,
        content: (
          <ConfirmModalContent
            icon={
              <Image preview={false} src={DeleteIcon} width={49} height={49} />
            }
            title={t('dicom.delete_dicom')}
            message={t('dicom.are_you_sure')}
            onOk={() => {
              deleteDicomLink({
                variables: {
                  id
                }
              });
            }}
            onCancel={() => Modal.destroyAll()}
          />
        ),
        icon: null,
        footer: null
      });
    },
    [deleteDicomLink, isDeleting, t]
  );

  const onClickShare = useCallback(
    (link: string) => {
      copyText(link);
      message.success(t('messages.copied'));
    },
    [copyText, message, t]
  );

  const columns: ColumnProps<DicomDoctorType>[] = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id'
      },
      {
        title: t('sessions.title'),
        dataIndex: 'name',
        key: 'name',
        render(value) {
          return value || '-';
        }
      },
      {
        title: t('sessions.file'),
        dataIndex: 'file',
        key: 'file'
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'actions',
        align: 'right',
        render: (id, record) => {
          const link = `${record.redirect_url}?sessionId=${record.session_id}`;

          return (
            <Flex align="center" justify="flex-end" gap={16}>
              <Button
                icon={<DeleteOutlined />}
                type="text"
                onClick={() => onClickDelete(id)}
                danger
              >
                {t('table.delete')}
              </Button>
              <Button
                icon={<ShareAltOutlined />}
                onClick={() => onClickShare(link)}
                // onClick={() => setIsOpenModal(true)}
              >
                {t('dicom.share')}
              </Button>
              <a target="_blank" href={link} rel="noreferrer">
                <Button icon={<EyeOutlined />} type="primary" ghost>
                  {t('dicom.view_dicom')}
                </Button>
              </a>
            </Flex>
          );
        }
      }
    ],
    [onClickDelete, t]
  );

  const results = data?.getDicomDoctor.data.results || [];
  const total = data?.getDicomDoctor.data.total || 0;

  return (
    <>
      <PageWrapper
        title="DICOM"
        color="white"
        extra={[
          <HowItWorksLink
            key="how-it-works"
            type={DOCTOR_HOW_IT_WORKS_PAGES.DICOM.key}
            title={t('common.how_it_works')}
          />
        ]}
      >
        <CreateDicom />
        <Title level={3}>{t('dicom.previous_dicoms')}</Title>
        {/* <Space size={16} style={{ marginBottom: 16 }}>
      <Input.Search
        style={{ width: 289 }}
        placeholder={t('dicom.search_by_file')}
        size="large"
      />
      <Space size={8}>
        <Button type="primary" size="large">
          {t('common.apply')}
        </Button>
        <Button type="default" size="large">
          {t('common.reset')}
        </Button>
      </Space>
    </Space> */}
        <Table
          dataSource={results}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            total,
            pageSize: PAGE_LIMIT,
            onChange: p => setPage(p - 1),
            showSizeChanger: false
          }}
          scroll={{ x: 768 }}
        />
      </PageWrapper>
      {/* {isOpenModal && <ShareDicomModal onClose={() => setIsOpenModal(false)} />} */}
    </>
  );
};

export default Dicom;
